import type { Granularity, KpisPerChannelResponse } from "../kpi-api-types.ts";
import type { DateScopeOverrideInput } from "./date-scope.ts";
import {
  getTracifyApiJson,
  type TracifyApiBasePath,
} from "./tracify-api-client.ts";
import { validateChannelKeysList, validateMetricKeysList } from "./metrics.ts";
import {
  type AnalyticsScope,
  createAnalyticsScopeFromTracifyContext,
} from "./scope.ts";
import type { TracifyContext } from "../ai/request-context.ts";

export type KpisPerChannelFetchInput = {
  channels?: string[] | null;
  granularity?: Granularity | null;
  requested_metrics?: string[] | null;
};

export type FetchKpisPerChannelOptions = {
  baseUrl: string;
  scope: AnalyticsScope;
  input: KpisPerChannelFetchInput;
  token: string;
  timeoutMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  requestId?: string;
  apiBasePath?: TracifyApiBasePath;
};

export type FetchKpisPerChannelWithContextOptions = Omit<
  FetchKpisPerChannelOptions,
  "scope" | "input"
> & {
  context: TracifyContext;
  input: KpisPerChannelFetchInput & DateScopeOverrideInput;
};

const DEFAULT_GRANULARITY: Granularity = "none";

function normalizeMetrics(requestedMetrics: string[] | null | undefined) {
  const validated = validateMetricKeysList(
    requestedMetrics,
    "requested_metrics"
  );
  return validated ? [...validated].sort() : null;
}

function normalizeChannels(
  channels: string[] | null | undefined,
  fieldName: string
) {
  const validated = validateChannelKeysList(channels, fieldName);
  return validated ? [...validated].sort() : null;
}

function appendGranularity(
  searchParams: URLSearchParams,
  granularity: Granularity
) {
  searchParams.append("granularity", granularity);
}

function appendFilters(searchParams: URLSearchParams, scope: AnalyticsScope) {
  if (scope.filters) {
    searchParams.append("filters", scope.filters);
  }
}

function appendCustomerTarget(
  searchParams: URLSearchParams,
  scope: AnalyticsScope
) {
  if (scope.customerType && scope.customerType !== "total") {
    searchParams.append("filter_and_sort_target", scope.customerType);
  }
}

function appendComparison(
  searchParams: URLSearchParams,
  comparison: { start: string; end: string } | null
) {
  if (!comparison) {
    return;
  }

  searchParams.append("compare_previous_date_range", "true");
  searchParams.append("compare_start_date", comparison.start);
  searchParams.append("compare_end_date", comparison.end);
}

export function buildKpisPerChannelSearchParams(
  scope: AnalyticsScope,
  input: KpisPerChannelFetchInput
): URLSearchParams {
  const granularity = input.granularity ?? DEFAULT_GRANULARITY;
  const requestedMetrics = normalizeMetrics(input.requested_metrics);
  const channels = normalizeChannels(
    input.channels ?? scope.channels ?? null,
    input.channels ? "channels" : "scope.channels"
  );
  const searchParams = new URLSearchParams();

  searchParams.append("preset_id", scope.presetId);
  searchParams.append("start_date", scope.startDate);
  searchParams.append("end_date", scope.endDate);

  for (const csid of scope.storeIds) {
    searchParams.append("csids", csid);
  }

  if (requestedMetrics) {
    for (const metric of requestedMetrics) {
      searchParams.append("requested_metrics", metric);
      searchParams.append("granularity_only_metrics", metric);
    }
  }

  appendGranularity(searchParams, granularity);
  appendFilters(searchParams, scope);
  appendCustomerTarget(searchParams, scope);
  appendComparison(searchParams, scope.comparison);

  if (channels) {
    for (const channel of channels) {
      searchParams.append("channels", channel);
    }
  }

  return searchParams;
}

export async function fetchKpisPerChannelWithScope({
  baseUrl,
  scope,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchKpisPerChannelOptions): Promise<KpisPerChannelResponse> {
  return getTracifyApiJson<KpisPerChannelResponse>({
    baseUrl,
    path: "/analytics/kpis/channels/",
    searchParams: buildKpisPerChannelSearchParams(scope, input),
    token,
    resource: "KPIs per channel",
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
}

export async function fetchKpisPerChannelWithContext({
  context,
  input,
  ...options
}: FetchKpisPerChannelWithContextOptions): Promise<KpisPerChannelResponse> {
  return fetchKpisPerChannelWithScope({
    ...options,
    scope: createAnalyticsScopeFromTracifyContext(context, input),
    input,
  });
}
